import QRCode from 'qrcode-svg';
import PrintableResult from './PrintableResult';
import { receiptLogoHtml } from './receiptLogo';
const HTML_ENTITIES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
/**
 * Content written through the Printable DSL is data, never markup — product and
 * counterparty names, comments, register metadata echoed from documents other
 * software authored. The web print path assigns the generated HTML to a live
 * page (PrintService → innerHTML), so every content string is encoded before it
 * is placed inside an element. Structural markup this class emits itself (the
 * `<br>` between cell lines, dividers, the QR SVG, the receipt logo block — whose only content is
 * a base64-validated PNG) is untouched.
 */
function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, ch => HTML_ENTITIES[ch]);
}
export default class PrintableHtml {
    printableContent;
    wrapperClass;
    textColor;
    getStyles() {
        return {
            sLine: {
                color: this.textColor,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                lineHeight: '1.1',
                minHeight: '10px',
                breakInside: 'avoid',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            },
            sAlignLeft: {
                justifyContent: 'flex-start',
                textAlign: 'left'
            },
            sAlignCenter: {
                justifyContent: 'center',
                textAlign: 'center'
            },
            sAlignRight: {
                justifyContent: 'flex-end',
                textAlign: 'right'
            },
            sDouble: {
                fontSize: '150%',
                fontWeight: '500'
            },
            sDoubleY: {
                transform: 'scale(1, 1.7)',
                lineHeight: '1.1'
            }
        };
    }
    constructor(printableContent, forceLightTheme, wrapperClass) {
        this.printableContent = printableContent;
        this.wrapperClass = wrapperClass;
        this.textColor = forceLightTheme ? '#000000' : 'var(--print-color)';
    }
    /**
     * Processes the printable content and generates formatted html.
     *
     * The method processes the printable content based on its type ('S', 'B', 'D', 'L', 'T')
     * and calls the corresponding process function.
     *
     * @returns {PrintableResult}
     */
    process() {
        const result = this.printableContent.map(c => {
            if (c.type === 'L') {
                const content = c.data[0].content[0] || '';
                const align = c.data[0].align || 'L';
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processLine(content, align, scale, style);
            }
            else if (c.type === 'S') {
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processSpaceBetween(c.data[0].content, scale, style);
            }
            else if (c.type === 'B') {
                const scale = c.data[0].scale || '1,1';
                const style = c.data[0].style || null;
                return this.processSpaceOrBreak(c.data[0].content, scale, style);
            }
            else if (c.type === 'D') {
                const delimiter = c.data[0].delimiter || ' ';
                const content = c.data[0].content[0] || '';
                const align = c.data[0].align || 'L';
                const scale = c.data[0].scale || '1,1';
                return this.processDivider(delimiter, content, align, scale);
            }
            else if (c.type === 'T') {
                const content = c.data || [];
                const colNumber = c.colNumber || 2;
                const colWidths = c.html?.colWidths || [];
                const style = c.html?.style;
                return this.processTable(content, colNumber, colWidths, style);
            }
            else if (c.type === 'QR') {
                const content = c.data[0].content[0] || '';
                const size = c.data[0].size || '40mm';
                return this.processQrCode(content, size);
            }
            else if (c.type === 'LOGO') {
                const block = receiptLogoHtml(c.data[0].content[0] || '');
                return block ? new PrintableResult([block], true, [{ width: 'normal', height: 'normal', bold: false }]) : null;
            }
            else {
                return null;
            }
        }).filter((c) => c !== null);
        const html = [
            !this.wrapperClass ? '<div>' : `<div class="${this.wrapperClass}">`,
            ...result.flatMap(r => r?.getLines() || []),
            '</div>'
        ];
        const highlight = [
            { width: 'normal', height: 'normal', bold: false },
            ...result.flatMap(r => r?.highlight || []),
            { width: 'normal', height: 'normal', bold: false }
        ];
        return new PrintableResult(html, true, highlight);
    }
    /**
     * Process the input data and create a formatted table as string.
     *
     * @param data The data to be inserted into the table. It should be an array of IPrintableContentData, each containing column data and options.
     * @param colNumber The number of columns in the table.
     * @param colWidths An optional array specifying the width of each column. If not provided, column widths will be calculated automatically.
     * @param tableStyle Table CSS style
     * @returns An array of string where each string is a row in the table.
     */
    processTable(data, colNumber, colWidths = [], tableStyle) {
        const style = this.createStyleString({
            borderCollapse: 'collapse',
            flex: '1',
            color: this.textColor,
            ...tableStyle,
        });
        let html = [`<div style="display: flex">`];
        html.push(`<table style="${style}">`);
        html.push('<colgroup>');
        for (let i = 0; i < colNumber; i++) {
            if (colWidths[i])
                html.push(`<col style="width: ${colWidths[i]}">`);
            else
                html.push(`<col>`);
        }
        html.push('</colgroup>');
        const cols = JSON.parse(JSON.stringify(data)).map((col) => {
            col.content = [col.content.map(escapeHtml).join('<br>')];
            if (!col.style)
                col.style = {};
            col.style.textAlign = 'left';
            if (col.align === 'C')
                col.style.textAlign = 'center';
            else if (col.align === 'R')
                col.style.textAlign = 'right';
            delete col.align;
            return col;
        });
        const rows = [];
        let curRow = [];
        const dividers = [];
        cols.forEach((col, index) => {
            if (col.content[0] === 'divider') {
                if (index === 0)
                    return;
                rows.push([...curRow]);
                dividers.push(rows.length);
                curRow = [];
                return;
            }
            const cellsUsed = curRow.reduce((a, b) => a + (b.colSpan || 1), 0);
            if ((cellsUsed + (col.colSpan || 1) <= colNumber)) {
                curRow.push(col);
            }
            else {
                rows.push([...curRow]);
                curRow = [col];
            }
            if (index === cols.length - 1) {
                rows.push([...curRow]);
            }
        });
        rows.forEach((row, index) => {
            if (dividers.includes(index)) {
                const sDivider = this.createStyleString({
                    marginBottom: '9px',
                    paddingTop: '9px',
                    borderBottom: '1px solid ' + this.textColor
                });
                html.push('<tr>' +
                    `<td colspan="${colNumber}">` +
                    `<div style="${sDivider}"></div>` +
                    '</td>' +
                    `</td>`);
            }
            html.push('<tr>');
            row.forEach(col => {
                const style = {
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.1',
                    minHeight: '10px',
                };
                const colStyle = this.createStyleString({
                    ...style,
                    ...col.style
                });
                const content = col.boxStyle
                    ? `<div style="${this.createStyleString(col.boxStyle)}">${col.content[0]}</div>`
                    : col.content[0];
                html.push(`<td style="${colStyle}" colspan="${col.colSpan || 1}">${content}</td>`);
            });
            html.push('</tr>');
        });
        html.push('</table>');
        html.push('</div>');
        html = [html.join('')];
        return new PrintableResult(html, true, [{
                width: 'normal',
                height: 'normal',
                bold: false
            }]);
    }
    /**
     * Formats the line according to the alignment
     *
     * @param content Content of the line
     * @param align The alignment of the text content ('L', 'R', or 'C')
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @param style CSS Style of the line
     * @returns PrintableResult
     */
    processLine(content, align = 'L', scale = '1,1', style = null) {
        const sLine = this.createStyleString(this.getStyles().sLine);
        const sAlignLeft = this.createStyleString(this.getStyles().sAlignLeft);
        const sAlignCenter = this.createStyleString(this.getStyles().sAlignCenter);
        const sAlignRight = this.createStyleString(this.getStyles().sAlignRight);
        let scaleClass = null;
        if (scale === '1,2')
            scaleClass = this.getStyles().sDoubleY;
        else if (scale === '2,2')
            scaleClass = this.getStyles().sDouble;
        const sScale = this.createStyleString(scaleClass);
        const sOther = this.createStyleString(style);
        let sAlign = sAlignLeft;
        if (align === 'C')
            sAlign = sAlignCenter;
        else if (align === 'R')
            sAlign = sAlignRight;
        const html = [`<div style="${sLine} ${sAlign} ${sScale} ${sOther}">${escapeHtml(content)}</div>`];
        return new PrintableResult(html, true, [{
                width: scale.split(',')[0] === '2' ? 'large' : 'normal',
                height: scale.split(',')[1] === '2' ? 'large' : 'normal',
                bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
            }]);
    }
    /**
     * Formats an array of two strings according to 'space-between' CSS rule
     *
     * @param arr Array of two strings
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @param style CSS Style of the line
     * @returns PrintableResult
     */
    processSpaceBetween(arr, scale = '1,1', style = null) {
        const sLine = this.createStyleString({
            ...this.getStyles().sLine,
            justifyContent: 'space-between'
        });
        let scaleClass = null;
        if (scale === '1,2')
            scaleClass = this.getStyles().sDoubleY;
        else if (scale === '2,2')
            scaleClass = this.getStyles().sDouble;
        const sScale = this.createStyleString(scaleClass);
        const sOther = this.createStyleString(style);
        const html = [
            `<div style="${sLine} ${sScale} ${sOther}">` +
                `<span>${escapeHtml(arr[0])}</span>` +
                `<span style="margin-left: auto;">${escapeHtml(arr[1])}</span>` +
                `</div>`
        ];
        return new PrintableResult(html, true, [{
                width: scale.split(',')[0] === '2' ? 'large' : 'normal',
                height: scale.split(',')[1] === '2' ? 'large' : 'normal',
                bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
            }]);
    }
    /**
     * Formats an array of two strings according to 'space-or-break' rule
     *
     * @param arr Array of two strings
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @param style CSS Style of the line
     * @returns PrintableResult
     */
    processSpaceOrBreak(arr, scale = '1,1', style = null) {
        const sLine = this.createStyleString(this.getStyles().sLine);
        let scaleClass = null;
        if (scale === '1,2')
            scaleClass = this.getStyles().sDoubleY;
        else if (scale === '2,2')
            scaleClass = this.getStyles().sDouble;
        const sScale = this.createStyleString(scaleClass);
        const sOther = this.createStyleString(style);
        const html = [`<div style="${sLine} ${sScale} ${sOther}">${escapeHtml(arr[0])} ${escapeHtml(arr[1])}</div>`];
        return new PrintableResult(html, true, [{
                width: scale.split(',')[0] === '2' ? 'large' : 'normal',
                height: scale.split(',')[1] === '2' ? 'large' : 'normal',
                bold: style?.fontWeight && parseFloat(style.fontWeight) > 400 ? true : false
            }]);
    }
    /**
     * Creates a divider line with text content
     *
     * @param delimiter The character used as a divider
     * @param content The text content
     * @param align The alignment of the text content ('L', 'R', or 'C')
     * @param scale Scale of the line: width,height. Default is '1,1'
     * @returns PrintableResult
     */
    processDivider(delimiter, content, align, scale = '1,1') {
        const sContainer = this.createStyleString({
            color: this.textColor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            lineHeight: '1.1',
            minHeight: '18px'
        });
        const sDividerLines = this.createStyleString({
            width: '100%'
        });
        const sDividerLineSolid = this.createStyleString({
            width: '100%',
            borderBottom: '1px solid',
            margin: '2px 0'
        });
        const sDividerLineDashed = this.createStyleString({
            width: '100%',
            borderBottom: '1.5px dashed',
            margin: '2px 0'
        });
        const sDividerLineUnderscore = this.createStyleString({
            width: '100%',
            borderBottom: '1px solid ' + this.textColor,
            margin: '2px 0',
            alignSelf: 'flex-end'
        });
        const sDividerText = this.createStyleString({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 'none'
        });
        const sPaddingLeft = this.createStyleString({
            paddingLeft: '13px'
        });
        const sPaddingRight = this.createStyleString({
            paddingRight: '13px'
        });
        let scaleClass = null;
        if (scale === '1,2')
            scaleClass = this.getStyles().sDoubleY;
        else if (scale === '2,2')
            scaleClass = this.getStyles().sDouble;
        const sScale = this.createStyleString(scaleClass);
        let html = [`<div style="${sContainer}">`];
        let dividerLineSolid = `<div style="${sDividerLines}">` +
            `<div style="${sDividerLineSolid}"></div>` +
            `</div>`;
        let dividerLineDoubleSolid = `<div style="${sDividerLines}">` +
            `<div style="${sDividerLineSolid}"></div>` +
            `<div style="${sDividerLineSolid}"></div>` +
            `</div>`;
        let dividerLineDashed = `<div style="${sDividerLines}">` +
            `<div style="${sDividerLineDashed}"></div>` +
            `</div>`;
        let dividerLineDoubleDashed = `<div style="${sDividerLines}">` +
            `<div style="${sDividerLineDashed}"></div>` +
            `<div style="${sDividerLineDashed}"></div>` +
            `</div>`;
        let dividerLineUnderscore = `<div style="${sDividerLineUnderscore}"></div>`;
        let dividerLines = null;
        if (delimiter === '-')
            dividerLines = dividerLineSolid;
        else if (delimiter === '=')
            dividerLines = dividerLineDoubleSolid;
        else if (delimiter === '--')
            dividerLines = dividerLineDashed;
        else if (delimiter === '==')
            dividerLines = dividerLineDoubleDashed;
        else if (delimiter === '_')
            dividerLines = dividerLineUnderscore;
        else {
            return new PrintableResult([''], true, [{
                    width: 'normal',
                    height: 'normal',
                    bold: false
                }]);
        }
        if (align !== 'L') {
            html.push(dividerLines);
        }
        if (content) {
            let padding = align !== 'L' ? sPaddingLeft : '';
            padding += align !== 'R' ? sPaddingRight : '';
            html.push(`<div style="${sDividerText} ${sScale} ${padding}">${escapeHtml(content)}</div>`);
        }
        if (align !== 'R') {
            html.push(dividerLines);
        }
        html.push('</div>');
        html = [html.join('')];
        return new PrintableResult(html, true, [{
                width: scale.split(',')[0] === '2' ? 'large' : 'normal',
                height: scale.split(',')[1] === '2' ? 'large' : 'normal',
                bold: false
            }]);
    }
    /**
     * Creates a QR code
     *
     * @param content The text content
     * @param size Qr Code size
     * @returns PrintableResult
     */
    processQrCode(content, size) {
        const sQrCodeContainer = this.createStyleString({
            textAlign: 'center',
            padding: '4px 0px',
            display: 'flex',
            justifyContent: 'center'
        });
        const sQrCode = this.createStyleString({
            width: '100%',
            imageRendering: 'pixelated',
        });
        const sQrCodeSize = this.createStyleString({
            maxWidth: size,
        });
        let options = {
            content: content,
            padding: 0,
            ecl: 'L',
            join: true,
            container: 'svg-viewbox'
        };
        let qrCode = new QRCode(options).svg();
        // Get the SVG QR code HTML as a string
        const html = [
            `<div style="${sQrCodeContainer}">` +
                `<div class="qr-code" style="${sQrCode} ${sQrCodeSize}">${qrCode}</div>` +
                '</div>'
        ];
        return new PrintableResult(html, true, [{
                width: 'normal',
                height: 'normal',
                bold: false
            }]);
    }
    createStyleString(style) {
        if (!style)
            return '';
        // Convert camelCase to kebab-case
        const styleString = Object.keys(style).map(key => {
            if (!style[key])
                return '';
            let kebabCaseKey = key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
            return `${kebabCaseKey}: ${style[key]}`;
        }).join('; ');
        if (!styleString)
            return '';
        return styleString + ';';
    }
}
//# sourceMappingURL=PrintableHtml.js.map